import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { getApiUrl } from '../../utils/apiConfig';

const Page = styled.div`
  min-height: 100vh;
  padding-top: clamp(90px, 8vw, 120px);
  background: ${props => props.theme.colors.backgroundLight};
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 1.5rem 3rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.06);
  padding: 1.25rem;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: .25rem;
`;

const Title = styled.h1`
  font-size: 1.4rem;
  margin: 0;
`;

const Price = styled.div`
  color: #065f46;
  background: #ecfdf5;
  border-radius: 999px;
  padding: .25rem .6rem;
  font-weight: 700;
  font-size: .9rem;
`;

export default function QuickCheckout() {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', childrenCount: 1, childrenNames: [''] });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(getApiUrl(`/public/courses/${encodeURIComponent(slug)}`));
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.error || 'Kurs konnte nicht geladen werden');
        if (alive) setCourse(data.data);
      } catch (e) {
        if (alive) setError(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  useEffect(() => {
    setForm(v => {
      const count = Math.max(1, parseInt(v.childrenCount || 1, 10));
      let names = v.childrenNames.slice(0, count);
      while (names.length < count) names.push('');
      return { ...v, childrenCount: count, childrenNames: names };
    });
  }, [form.childrenCount]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!course) return;
    const missing = form.childrenNames.findIndex(n => !String(n || '').trim());
    if (missing !== -1) { setError('Bitte geben Sie alle Kindernamen an.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(getApiUrl(`/public/courses/${course.id}/create-checkout`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: form.name,
          customer_email: form.email,
          customer_phone: form.phone,
          children: form.childrenNames.map(n => ({ name: n })),
          children_count: form.childrenCount
        })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Checkout konnte nicht erstellt werden');
      if (data.data?.checkout_url) {
        window.location.assign(data.data.checkout_url);
        return;
      }
      throw new Error('Unerwartete Antwort vom Server.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  const unitPrice = course ? (course.price || 0) : 0;
  const currency = course ? (course.currency || 'EUR') : 'EUR';
  const priceFmt = new Intl.NumberFormat('de-DE', { style: 'currency', currency });
  const available = (course?.available_spots ?? Infinity);
  const cappedCount = Math.min(form.childrenCount || 1, available);
  const total = priceFmt.format(unitPrice * cappedCount);
  const unitFmt = priceFmt.format(unitPrice);

  return (
    <Page>
      <Helmet>
        <title>Schnellbuchung – {course ? course.title : 'Kurs'}</title>
      </Helmet>
      <Container>
        <Card>
          {loading && <div>Wird geladen…</div>}
          {error && <div style={{ color: '#b91c1c' }}>{error}</div>}
          {!loading && course && (
            <>
              <TitleRow>
                <Title>Jetzt schnell buchen</Title>
                <Price>{unitFmt} pro Kind</Price>
              </TitleRow>
              <div style={{ color: '#6b7280', marginBottom: '1rem' }}>{course.title}</div>
              <form onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gap: '.75rem' }}>
                  <input required placeholder="Ihr Name" value={form.name} onChange={e => setForm(v => ({ ...v, name: e.target.value }))} style={inputStyle} />
                  <input required type="email" placeholder="E-Mail" value={form.email} onChange={e => setForm(v => ({ ...v, email: e.target.value }))} style={inputStyle} />
                  <input placeholder="Telefon (optional)" value={form.phone} onChange={e => setForm(v => ({ ...v, phone: e.target.value }))} style={inputStyle} />
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: '.5rem' }}>
                    <input type="number" min={1} step={1} max={available} value={form.childrenCount} onChange={e => setForm(v => ({ ...v, childrenCount: Math.max(1, Math.min(available, parseInt(e.target.value || '1', 10))) }))} style={inputStyle} placeholder="Anzahl der Kinder" />
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', color:'#374151', fontWeight:700, background:'#f9fafb', borderRadius:10 }}>{total} gesamt</div>
                  </div>
                  <div style={{ display:'grid', gap: '.5rem' }}>
                    {Array.from({ length: form.childrenCount }).map((_, idx) => (
                      <input key={idx} required placeholder={`Name des Kindes ${idx+1}`} value={form.childrenNames[idx] || ''}
                        onChange={e => setForm(v => { const names = v.childrenNames.slice(); names[idx] = e.target.value; return { ...v, childrenNames: names }; })}
                        style={inputStyle} />
                    ))}
                  </div>
                  {error && <div style={{ color: '#b91c1c', fontSize: '.9rem' }}>{error}</div>}
                  <button disabled={submitting || available <= 0} style={{ ...payBtnStyle, opacity: (submitting || available <= 0) ? .6 : 1, cursor: (submitting || available <= 0) ? 'not-allowed' : 'pointer' }}>
                    {available <= 0 ? 'Ausgebucht' : (submitting ? 'Wird verarbeitet…' : 'Jetzt sicher bezahlen')}
                  </button>
                  <div style={{ fontSize: '.85rem', color: '#6b7280', textAlign: 'center' }}>Sichere Zahlung über Stripe</div>
                </div>
              </form>
            </>
          )}
        </Card>
      </Container>
    </Page>
  );
}

const inputStyle = {
  width: '100%',
  padding: '.85rem 1rem',
  border: '2px solid #e5e7eb',
  borderRadius: '10px',
  outline: 'none',
  background: '#fff'
};

const payBtnStyle = {
  width: '100%',
  padding: '1rem 1.25rem',
  background: 'linear-gradient(45deg, #2ecc71, #27ae60)',
  color: '#fff',
  border: 0,
  borderRadius: '12px',
  fontWeight: 700,
  cursor: 'pointer',
  boxShadow: '0 8px 25px rgba(46,204,113,.35)'
};
